import { useEffect, useRef, useState, useMemo } from 'react';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { scheduleI18n } from './i18n';
import { useNavigate } from 'react-router-dom';
import {
  getAppointmentPaged,
  getConnectionStatus,
  softDeleteAppointment,
} from 'services/scheduleServices';
import { getCurrentUser } from 'services/authService';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import ModalCreateEditEvent from './partials/ModalCreateEditEvent';
import DatePickerEs from 'components/DatePickerEs/DatePickerEs';
import outlook from '../../assets/images/Outlook-Icon-FY26.svg';
import './schedule.css';
import ConfirmModal from 'components/ConfirmModal/ConfirmModal';
import usePermissionsAgenda from 'permissions/modules/agenda/usePermissionsAgenda';

const Schedule = ({ DesignSystemComponents }) => {
  const { Card, Button, Input, Table, SvgImage, IconButton, Dropdown } =
    DesignSystemComponents;

  const navigate = useNavigate();
  const debounceRef = useRef(null);

  const { t, language: schLanguage } = useScopedTranslation(scheduleI18n);

  const originOptions = useMemo(
    () => [
      { label: t('schedule_opt_legal365'), value: 'legal365' },
      { label: t('schedule_opt_outlook'), value: 'outlook' },
      { label: t('schedule_opt_all'), value: 'all' },
    ],
    [schLanguage]
  );

  const agendaOptions = useMemo(
    () => [
      { label: t('schedule_opt_mySchedule'), value: 'mine' },
      { label: t('schedule_opt_allSchedules'), value: 'all' },
    ],
    [schLanguage]
  );

  const [showCreate, setShowCreate] = useState(false);
  const [outlookConnected, setOutlookConnected] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [sortKey, setSortKey] = useState('StartAtUtc');
  const [sortDirection, setSortDirection] = useState('desc');
  const [userId, setUserId] = useState(null);
  const [filterByDate, setFilterByDate] = useState(null);
  const [selectedOrigin, setSelectedOrigin] = useState(originOptions[0]);
  const [selectedAgenda, setSelectedAgenda] = useState(agendaOptions[0]);
  const [editingId, setEditingId] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const {
    canDownloadExcel,
    canCreateAppointment,
    canManageAppointment,
    canConnectOutlook,
    canViewOthersCalendar,
  } = usePermissionsAgenda();

  const canSeeAllSchedules = canViewOthersCalendar;
  const showAgendaDropdown = canSeeAllSchedules;
  const showOriginDropdown = outlookConnected?.connected === true;
  let searchCol = 5;
  if (showAgendaDropdown && showOriginDropdown) {
    searchCol = 3;
  } else if (showAgendaDropdown || showOriginDropdown) {
    searchCol = 5;
  }

  const fetchOutlookConnection = async () => {
    try {
      const result = await getConnectionStatus.getStatus();
      setOutlookConnected(result);
    } catch (error) {
      console.warn('Error loading Outlook connection status:', error);
    }
  };

  const handleSearchChange = (text) => {
    setSearchDraft(text);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      setPage(0);
      setSearchTerm(text);
    }, 350);
  };

  const buildUtcRange = () => {
    if (!filterByDate?.startDate || !filterByDate?.endDate) {
      return { fromUtc: null, toUtc: null };
    }

    const from = new Date(filterByDate.startDate);
    from.setHours(0, 0, 0, 0);

    const to = new Date(filterByDate.endDate);
    to.setHours(23, 59, 59, 999);

    return {
      fromUtc: from.toISOString(),
      toUtc: to.toISOString(),
    };
  };

  const fetchAppointments = async () => {
    try {
      setTableLoading(true);

      const { fromUtc, toUtc } = buildUtcRange();

      const result = await getAppointmentPaged.getPaged(
        page + 1,
        pageSize,
        searchTerm,
        canSeeAllSchedules && selectedAgenda?.value === 'all' ? null : userId,
        sortKey,
        sortDirection === 'desc',
        fromUtc,
        toUtc,
        selectedOrigin?.value
      );

      setAppointments(result.items || []);
      setTotalCount(result.total || 0);
    } catch (error) {
      console.warn('Error fetching appointments', error);
      setAppointments([]);
      setTotalCount(0);
    } finally {
      setTableLoading(false);
    }
  };

  const formatDate = (date) => {
    const d = String(date.getDate()).padStart(2, '0');
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const y = date.getFullYear();
    return `${d}/${m}/${y}`;
  };

  const formatTime = (date) => {
    const h = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    return `${h}:${min}`;
  };

  const fetchAllAppointments = async () => {
    const pageSizeToUse = 200;
    let pageNumber = 1;
    let allItems = [];
    let totalItems = 0;

    const { fromUtc, toUtc } = buildUtcRange();

    try {
      while (true) {
        const result = await getAppointmentPaged.getPaged(
          pageNumber,
          pageSizeToUse,
          searchTerm,
          canSeeAllSchedules && selectedAgenda?.value === 'all' ? null : userId,
          sortKey,
          sortDirection === 'desc',
          fromUtc,
          toUtc,
          selectedOrigin?.value
        );

        const items = result.items || [];
        const total = result.total || 0;

        if (pageNumber === 1) totalItems = total;

        allItems = [...allItems, ...items];

        if (allItems.length >= totalItems) break;

        pageNumber++;
      }
    } catch (error) {
      console.error('Error fetching all events:', error);
    }

    return allItems;
  };

  const downloadExcel = async () => {
    try {
      const allData = await fetchAllAppointments();
      if (!allData.length) return;

      const formatted = allData.map((item) => {
        const start = parseUtc(item.startAtUtc);
        const end = parseUtc(item.endAtUtc);

        return {
          [t('schedule_excelCol_start')]: start
            ? `${formatDate(start)} ${formatTime(start)}`
            : '--',
          [t('schedule_excelCol_end')]: end
            ? `${formatDate(end)} ${formatTime(end)}`
            : '--',
          [t('schedule_excelCol_origin')]:
            item.origin === 'legal365'
              ? t('schedule_origin_legal365')
              : t('schedule_origin_outlook'),
          [t('schedule_excelCol_title')]:
            item.origin === 'legal365'
              ? item.title
              : t('schedule_viewInOutlook'),
          [t('schedule_excelCol_description')]:
            item.origin === 'outlook'
              ? t('schedule_viewInOutlook')
              : item.description || '--',
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(formatted);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        t('schedule_excelSheet')
      );

      const excelBuffer = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
      });

      const blob = new Blob([excelBuffer], {
        type: 'application/octet-stream',
      });

      saveAs(blob, t('schedule_excelFile'));
    } catch (error) {
      console.error('Error generating Excel:', error);
    }
  };

  const handleEdit = (id) => {
    setEditingId(id);
    setShowCreate(true);
  };

  const handleCloseModal = () => {
    setShowCreate(false);
    setEditingId(null);
  };

  const handleDeleteClick = (id) => {
    setDeletingId(id);
    setShowDelete(true);
  };

  const handleDeleteConfirmed = async () => {
    if (!deletingId) return;

    try {
      setDeleting(true);

      await softDeleteAppointment.deleteAppointment(deletingId);

      setShowDelete(false);
      setDeletingId(null);

      if (appointments.length === 1 && page > 0) {
        setPage((prev) => prev - 1);
      } else {
        fetchAppointments();
      }
    } catch (error) {
      console.warn(error.customMessage || error.message);
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    const authUser = getCurrentUser();
    if (authUser?.userId) {
      setUserId(authUser.userId);
    }
  }, []);

  useEffect(() => {
    fetchOutlookConnection();
  }, []);

  useEffect(() => {
    if (!userId) return;
    fetchAppointments();
  }, [
    userId,
    page,
    pageSize,
    searchTerm,
    sortKey,
    sortDirection,
    filterByDate,
    selectedOrigin?.value,
    selectedAgenda,
  ]);

  useEffect(() => {
    setPage(0);
  }, [filterByDate]);

  const parseUtc = (value) => {
    if (!value) return null;
    return new Date(`${value}Z`);
  };

  const columns = [
    ...(canSeeAllSchedules && selectedAgenda?.value === 'all'
      ? [
          {
            accessor: 'userName',
            label: t('schedule_col_user'),
            sort: true,
            render: (row) => <p className='caption'>{row.userName || '--'}</p>,
          },
        ]
      : []),

    {
      label: t('schedule_col_start'),
      sort: true,
      render: (row) => {
        const date = parseUtc(row.startAtUtc);
        if (!date) return '--';

        return (
          <div className='d-flex flex-row'>
            <p className='caption me-2'>{formatDate(date)}</p>
            <p className='caption'>{formatTime(date)}</p>
          </div>
        );
      },
    },
    {
      label: t('schedule_col_end'),
      sort: true,
      render: (row) => {
        const date = parseUtc(row.endAtUtc);
        if (!date) return '--';

        return (
          <div className='d-flex flex-row'>
            <p className='caption me-2'>{formatDate(date)}</p>
            <p className='caption'>{formatTime(date)}</p>
          </div>
        );
      },
    },
    ...(outlookConnected?.connected === true
      ? [
          {
            accessor: 'origin',
            label: t('schedule_col_origin'),
            sort: true,
            render: (row) => (
              <p className='caption'>
                {row.origin === 'legal365'
                  ? t('schedule_origin_legal365')
                  : t('schedule_origin_outlook')}
              </p>
            ),
          },
        ]
      : []),

    {
      label: t('schedule_col_title'),
      sort: true,
      render: (row) => (
        <p className='caption'>
          {row.origin === 'legal365'
            ? row.title
            : t('schedule_viewInOutlook')}
        </p>
      ),
    },
    {
      label: t('schedule_col_description'),
      render: (row) => (
        <p className='caption'>
          {row.origin === 'outlook'
            ? t('schedule_viewInOutlook')
            : row.description || '--'}
        </p>
      ),
    },
    {
      label: t('schedule_col_actions'),
      render: (row) => {
        const isMine = row.userId === userId;
        const isEditable = row.origin === 'legal365';

        return (
          <>
            {!isMine || !isEditable ? (
              <p className='caption'>--</p>
            ) : (
              <div className='d-flex'>
                <IconButton
                  ariaLabel={t('schedule_aria_edit')}
                  icon='pencil'
                  onClick={() => handleEdit(row.id)}
                  size='md'
                  iconStroke='var(--primary-500)'
                  disabled={!canManageAppointment}
                />
                <IconButton
                  ariaLabel={t('schedule_aria_delete')}
                  icon='trash'
                  onClick={() => handleDeleteClick(row.id)}
                  size='md'
                  iconStroke='var(--error-500)'
                  className='ms-1'
                  disabled={!canManageAppointment}
                />
              </div>
            )}
          </>
        );
      },
    },
  ];

  useEffect(() => {
    setSortKey('StartAtUtc');
    setSortDirection('desc');
    setPage(0);
  }, [selectedAgenda]);

  return (
    <div>
      <div className='d-flex justify-content-between align-items-start '>
        <div className='mb-4'>
          <h2 className=' fw-semibold'>{t('schedule_title')}</h2>
        </div>
        <div className='d-flex gap-3 align-items-center'>
          {outlookConnected.connected === false ? (
            <Button
              label={t('schedule_connectOutlook')}
              onClick={() => navigate('/mi-perfil')}
              variant='secondary'
              size='sm'
              disabled={!canConnectOutlook}
            />
          ) : (
            <div title={t('schedule_connectedOutlook')}>
              <SvgImage className='outlook-logo' src={outlook} />
            </div>
          )}
          <Button
            label={t('schedule_newEvent')}
            size='sm'
            variant='primary'
            iconStart='plusLg'
            onClick={() => setShowCreate(true)}
            disabled={!canCreateAppointment}
          />
        </div>
      </div>
      <Card specialPadding='p-4'>
        <div className='d-flex justify-content-end'>
          <Button
            iconStart='download'
            label={t('schedule_downloadExcel')}
            variant='naked'
            className='mb-2'
            size='sm'
            onClick={downloadExcel}
            disabled={tableLoading || !userId || !canDownloadExcel}
          />
        </div>
        <div className='row'>
          <div className={`col-${searchCol}`}>
            <Input
              icon='search'
              placeholder={t('schedule_searchPlaceholder')}
              value={searchDraft}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
          {showAgendaDropdown && (
            <div className='col-2'>
              <Dropdown
                options={agendaOptions}
                value={selectedAgenda}
                onChange={(selected) => {
                  setSelectedAgenda(selected);
                  setPage(0);
                }}
              />
            </div>
          )}
          {showOriginDropdown && (
            <div className='col-2'>
              <Dropdown
                options={originOptions}
                value={selectedOrigin}
                onChange={(selected) => {
                  setSelectedOrigin(selected);
                  setPage(0);
                }}
              />
            </div>
          )}
          <div className='col-3'>
            <DatePickerEs
              initialRange={filterByDate}
              value={filterByDate}
              onChange={setFilterByDate}
            />
          </div>
          <div className='col-2'>
            <div className='d-flex justify-content-end'>
              <IconButton
                icon='calendarDate2'
                onClick={() =>
                  navigate('/calendar', {
                    state: { defaultTab: 'second' },
                  })
                }
                size='lg'
                iconStroke='var(--primary-600)'
              />
            </div>
          </div>
        </div>
        <div
          className={`table-container ${
            selectedAgenda?.value === 'all'
              ? outlookConnected?.connected
                ? 'table-schedule-with-outlook-all'
                : 'table-schedule-all'
              : outlookConnected?.connected
                ? 'table-schedule-with-outlook'
                : 'table-schedule'
          }`}
        >
          <Table
            columns={columns}
            data={appointments}
            initialPageSize={pageSize}
            page={page}
            onPageChange={setPage}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setPage(0);
            }}
            paginator
            paginatorGroup={[
              { label: '10', value: 10 },
              { label: '20', value: 20 },
              { label: '50', value: 50 },
            ]}
            paginationMode='server'
            totalItems={totalCount}
            loading={tableLoading}
            serverSortKey={sortKey}
            serverSortDirection={sortDirection}
            onServerSortChange={({ sortKey: key, direction }) => {
              setSortKey(key);
              setSortDirection(direction);
              setPage(0);
            }}
            moreLines
          />
        </div>
      </Card>
      <ModalCreateEditEvent
        DesignSystemComponents={DesignSystemComponents}
        show={showCreate}
        onClose={handleCloseModal}
        onCreated={fetchAppointments}
        appointmentId={editingId}
      />
      <ConfirmModal
        Modal={DesignSystemComponents.Modal}
        show={showDelete}
        title={t('schedule_delete_title')}
        message={t('schedule_delete_message')}
        confirmLabel={deleting ? t('schedule_delete_deleting') : t('schedule_delete_confirm')}
        confirmVariant='danger'
        onConfirm={handleDeleteConfirmed}
        onClose={() => {
          if (deleting) return;
          setShowDelete(false);
          setDeletingId(null);
        }}
      />
    </div>
  );
};

export default Schedule;
